import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { StoreCategory } from './useData';

function formatAmount(v: string | number | null | undefined): string {
  if (v === '' || v === null || v === undefined) return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export const moduleConfig = {
  module: {
    title: 'Stores Categories',
    permissionModuleKey: 'store_categories',
    route: {
      list: '/parameters/purchasing?section=supplies-categories',
      new: '/parameters/store-categories/new',
      detail: (id: number) => `/parameters/store-categories/${id}`,
      edit: (id: number) => `/parameters/store-categories/${id}/edit`},
    api: {
      list: '/store-categories',
      detail: (id: number) => `/store-categories/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name, abbreviation, approval type…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['name', 'isEnableAprv']},
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        getValue: (item: StoreCategory) => item.name,
        options: []},
      {
        key: 'isEnableAprv',
        label: 'Approval enabled',
        type: 'boolean' as const,
        getValue: (item: StoreCategory) => (item.isEnableAprv ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: StoreCategory[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isEnableAprv?.length) {
          const s = item.isEnableAprv ? 'Yes' : 'No';
          if (!filters.isEnableAprv.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New category',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/store-categories/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: StoreCategory) => item.id,
  },
  emptyStates: {
    noData: 'Add a store category for requisitions and supply cases.',
    noSearchResults: 'No categories match your search.',
    noFilterResults: 'No categories match the filters.',
  },
  delete: {
    title: 'Delete store category',
    message: 'Delete this store category?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete store category.',
    handler: async (id: number) => {
      const { deleteStoreCategory } = await import('../../../services/mutations');
      await deleteStoreCategory(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This store category does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Store category',
    getTitle: (item: StoreCategory) => item.name || `Store category #${item.id}`},
  form: {
    new: { title: 'New store category', subtitle: 'Define approval buckets for supplies', submitText: 'Create' },
    edit: { title: 'Edit store category', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<StoreCategory>) => {
      const { createStoreCategory } = await import('../../../services/mutations');
      return createStoreCategory(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<StoreCategory>) => {
      const { updateStoreCategory } = await import('../../../services/mutations');
      return updateStoreCategory(id, data as Record<string, unknown>);
    }},
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Category name',
      validation: { required: true, minLength: 1, maxLength: 200 }},
    {
      name: 'abrv',
      label: 'Abbreviation',
      type: 'text',
      placeholder: 'Short code',
      validation: { maxLength: 50 }},
    {
      name: 'isEnableAprv',
      label: 'Approval rules',
      type: 'switch',
      switchLabel: 'Enable approval workflow',
      defaultValue: false},
    {
      name: 'aprvType',
      label: 'Approval type',
      type: 'text',
      placeholder: 'Optional',
      validation: { maxLength: 100 }},
    {
      name: 'aprvAmnt',
      label: 'Approval amount limit',
      type: 'number',
      placeholder: 'Optional limit'},
    {
      name: 'aprvUser',
      label: 'Approver user ID',
      type: 'number',
      placeholder: 'Optional user id'},
    {
      name: 'aprvDpt',
      label: 'Approver department',
      type: 'text',
      placeholder: 'Optional',
      validation: { maxLength: 100 }},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Store category',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['name', 'abrv', 'isEnableAprv', 'aprvType', 'aprvAmnt', 'aprvUser', 'aprvDpt'],
        fieldSpans: {
          name: 'md:col-span-8',
          abrv: 'md:col-span-4',
          isEnableAprv: 'md:col-span-12',
          aprvType: 'md:col-span-6',
          aprvAmnt: 'md:col-span-3',
          aprvUser: 'md:col-span-3',
          aprvDpt: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<StoreCategory>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'abrv', header: 'Abrv', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isEnableAprv',
    header: 'Approval',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isEnableAprv ? 'success' : 'secondary'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isEnableAprv ? 'Yes' : 'No'}
      </Badge>
    )},
  {
    key: 'aprvAmnt',
    header: 'Amount limit',
    sortable: true,
    render: (_v, row) => <TextCell value={formatAmount(row?.aprvAmnt)} />},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: StoreCategory, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Name', value: item.name },
      { label: 'Abbreviation', value: item.abrv || '' },
      { label: 'Approval enabled', value: item.isEnableAprv ? 'Yes' : 'No' },
      { label: 'Approval type', value: item.aprvType || '' },
      { label: 'Approval amount limit', value: formatAmount(item.aprvAmnt) },
      { label: 'Approver user ID', value: item.aprvUser != null ? String(item.aprvUser) : '' },
      { label: 'Approver department', value: item.aprvDpt || '' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const TableActionsCell: React.FC<{
  item: StoreCategory;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: StoreCategory,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<StoreCategory> => ({
  name: '',
  abrv: '',
  isEnableAprv: false,
  aprvType: '',
  aprvAmnt: undefined,
  aprvUser: undefined,
  aprvDpt: ''});

export const mapItemToFormData = (item: StoreCategory): Partial<StoreCategory> => ({
  name: item.name,
  abrv: item.abrv ?? '',
  isEnableAprv: item.isEnableAprv ?? false,
  aprvType: item.aprvType ?? '',
  aprvAmnt:
    item.aprvAmnt !== undefined && item.aprvAmnt !== null && item.aprvAmnt !== ''
      ? Number(item.aprvAmnt)
      : undefined,
  aprvUser: item.aprvUser ?? undefined,
  aprvDpt: item.aprvDpt ?? ''});

export type ModuleConfig = typeof moduleConfig;
